import React from 'react';
import axios from 'axios';
import { IconHeading } from '@app/components/IconHeading';
import { ExclamationCircleIcon, MapMarkerIcon } from '@patternfly/react-icons';
import { FormGroup, TextInput, Select, SelectVariant } from "@patternfly/react-core";
import { FormGroupSpacer } from '../../../components/forms/pf/form-group-spacer.component';
import { Card, CardBody, CardHeader, Col, Input, Row } from 'reactstrap';
import { getRegionOptions } from '@app/common/world';

// Declare Google Maps global variable
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

class SiteInfo extends React.Component<any, any> {
  addressSearchTimeout: any = null;
  addressCache: Map<string, any[]> = new Map();
  autocompleteService: any = null;

  constructor(props: any) {
    super(props);
    this.state = {
      addressSuggestions: [],
      showAddressSuggestions: false,
      isLoadingAddressSuggestions: false,
    };
    
    // Bind methods to ensure proper context
    this.handleAddressChange = this.handleAddressChange.bind(this);
    this.selectAddressSuggestion = this.selectAddressSuggestion.bind(this);
    this.hideAddressSuggestions = this.hideAddressSuggestions.bind(this);
    this.searchAddress = this.searchAddress.bind(this);
    this.initGoogleMaps = this.initGoogleMaps.bind(this);
  }

  componentDidMount() {
    this.loadGoogleMapsScript();
  }

  componentWillUnmount() {
    if (this.addressSearchTimeout) {
      clearTimeout(this.addressSearchTimeout);
    }
  }

  // Load Google Maps JavaScript SDK
  loadGoogleMapsScript() {
    const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
    
    if (!GOOGLE_API_KEY) {
      // console.log('Google API key not found, using geocoding fallback');
      return;
    }

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      this.initGoogleMaps();
      return;
    }

    // FIX: Check if script is already being loaded to prevent duplicate API calls
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script is already loading, wait for it
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          this.initGoogleMaps();
        }
      }, 100);
      return;
    }

    // Only create new script if none exists
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&callback=initMap&region=CA`;
    script.async = true;
    script.defer = true;
    
    // Handle script loading errors
    script.onerror = () => {
      // console.log('Google Maps script failed to load, using geocoding fallback');
    };
    
    window.initMap = this.initGoogleMaps;
    
    document.head.appendChild(script);
    
    // Timeout fallback if Google takes too long
    setTimeout(() => {
      if (!this.autocompleteService) {
        // console.log('Google Maps taking too long, fallback ready');
      }
    }, 5000);
  }

  initGoogleMaps() {
    try {
      if (window.google && window.google.maps && window.google.maps.places) {
        this.autocompleteService = new window.google.maps.places.AutocompleteService();
        // console.log('Google Places service initialized successfully');
      } else {
        // console.log('Google Maps API not fully loaded, using fallback');
      }
    } catch (error) {
      // console.error('Error initializing Google Maps:', error);
      // console.log('Using geocoding fallback due to Google Maps error');
    }
  }

  // Parse Google Place details into our address format
  parseGooglePlaceDetails(place: any) {
    const components = place.address_components || [];
    
    const getComponent = (types: string[]) => {
      const component = components.find((comp: any) => 
        types.some((type: string) => comp.types.includes(type))
      );
      return component ? component.long_name : '';
    };

    const getShortComponent = (types: string[]) => {
      const component = components.find((comp: any) => 
        types.some((type: string) => comp.types.includes(type))
      );
      return component ? component.short_name : '';
    };

    let state = getComponent(['administrative_area_level_1']);
    let country = getComponent(['country']);

    // Handle Canadian provinces - normalize Quebec variations
    if (country === 'Canada' || country === 'CA') {
      country = 'Canada';
      
      // Handle Quebec variations (French/English)
      if (state === 'Québec' || state === 'Quebec' || state === 'QC') {
        state = 'Quebec';
      }
      // Handle other Canadian provinces
      else if (state === 'Ontario' || state === 'ON') {
        state = 'Ontario';
      }
      else if (state === 'British Columbia' || state === 'BC') {
        state = 'British Columbia';
      }
      else if (state === 'Alberta' || state === 'AB') {
        state = 'Alberta';
      }
      // Add more province mappings as needed
    }

    return {
      house_number: getComponent(['street_number']),
      road: getComponent(['route']),
      city: getComponent(['locality', 'sublocality', 'administrative_area_level_3']),
      state: state,
      country: country,
      postcode: getComponent(['postal_code'])
    };
  }

  // Improved address parsing
  parseMapboxAddress(feature: any) {
    const context = feature.context || [];
    
    const getContext = (idPattern: string) => {
      const item = context.find((c: any) => c.id.includes(idPattern));
      return item ? item.text : '';
    };

    let city = getContext('place') || '';
    let state = getContext('region') || '';
    let country = getContext('country') || '';
    let postcode = getContext('postcode') || '';

    // Handle Canadian addresses specifically
    if (country === 'Canada') {
      // Quebec variations
      if (state === 'Québec' || state === 'Quebec' || state === 'QC') {
        state = 'Quebec';
      }
      // Other provinces
      else if (state === 'Ontario' || state === 'ON') {
        state = 'Ontario';
      }
      else if (state === 'British Columbia' || state === 'BC') {
        state = 'British Columbia';
      }
      else if (state === 'Alberta' || state === 'AB') {
        state = 'Alberta';
      }
    }

    return {
      house_number: feature.address || '',
      road: feature.text || '',
      city: city,
      state: state,
      country: country,
      postcode: postcode
    };
  }

  // Fast geocoding 
  async searchWithGeocoding(query: string) {
    const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    
    if (!MAPBOX_ACCESS_TOKEN) {
      // console.log('Mapbox API token not found, trying OpenStreetMap fallback');
      return this.searchWithOpenStreetMap(query);
    }

    try {
      // console.log('SiteInfo - Using Mapbox geocoding API...'); 
      
      // Using Mapbox geocoding API (requires API key)
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
        {
          params: {
            access_token: MAPBOX_ACCESS_TOKEN,
            autocomplete: true,
            limit: 5,
            types: 'address',
            country: 'CA,US', // Focus on Canada and US for better results
          },
          timeout: 3000,
        }
      );

      // console.log('Mapbox response:', response.data);

      const suggestions = response.data.features?.map((feature: any) => ({
        display_name: feature.place_name,
        address: this.parseMapboxAddress(feature),
        isMapboxPlace: true
      })) || [];

      this.addressCache.set(query.toLowerCase().trim(), suggestions);
      
      this.setState({
        addressSuggestions: suggestions,
        showAddressSuggestions: suggestions.length > 0,
        isLoadingAddressSuggestions: false,
      });

    } catch (error) {
      console.error('Mapbox geocoding error:', error);
      return this.searchWithOpenStreetMap(query);
    }
  }

  // OpenStreetMap fallback method
  async searchWithOpenStreetMap(query: string) {
    try {
      console.log('Using OpenStreetMap as fallback...');
      const osmResponse = await axios.get(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1&countrycodes=ca,us`,
        { timeout: 3000 }
      );
      
      const osmSuggestions = osmResponse.data?.map((item: any) => ({
        display_name: item.display_name,
        address: this.parseOSMAddress(item.address || {})
      })) || [];
      
      this.addressCache.set(query.toLowerCase().trim(), osmSuggestions);
      
      this.setState({
        addressSuggestions: osmSuggestions,
        showAddressSuggestions: osmSuggestions.length > 0,
        isLoadingAddressSuggestions: false,
      });
      
    } catch (osmError) {
      console.error('All geocoding services failed:', osmError);
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
    }
  }

  // Parse address format
  parseOSMAddress(address: any) {
    let state = address.state || address.province || '';
    let country = address.country || '';

    // Handle Canadian provinces - normalize Quebec variations
    if (country === 'Canada') {
      if (state === 'Québec' || state === 'Quebec' || state === 'QC') {
        state = 'Quebec';
      }
      else if (state === 'Ontario' || state === 'ON') {
        state = 'Ontario';
      }
      else if (state === 'British Columbia' || state === 'BC') {
        state = 'British Columbia';
      }
      else if (state === 'Alberta' || state === 'AB') {
        state = 'Alberta';
      }
    }

    return {
      house_number: address.house_number || '',
      road: address.road || address.street || '',
      city: address.city || address.town || address.village || '',
      state: state,
      country: country,
      postcode: address.postcode || address.postal_code || ''
    };
  }

  // Google Places SDK method
  async searchWithGooglePlaces(query: string) {
    if (!this.autocompleteService) {
      console.log('Google Places not ready, using fallback');
      return this.searchWithGeocoding(query);
    }

    try {
      const request = {
        input: query,
        types: ['address'],
      };

      this.autocompleteService.getPlacePredictions(request, (predictions: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const suggestions = predictions.map((prediction: any) => ({
            display_name: prediction.description,
            place_id: prediction.place_id,
            address: {}, // Will be filled when selected
            isGooglePlace: true 
          }));

          this.addressCache.set(query.toLowerCase().trim(), suggestions);
          
          this.setState({
            addressSuggestions: suggestions,
            showAddressSuggestions: suggestions.length > 0,
            isLoadingAddressSuggestions: false,
          });
        } else {
          // console.log('Google Places API error, using fallback');
          this.searchWithGeocoding(query);
        }
      });

    } catch (error) {
      // console.error('Google Places error, using fallback:', error);
      this.searchWithGeocoding(query);
    }
  }

  // Get detailed place information from Google Places
  async getGooglePlaceDetails(placeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        reject(new Error('Google Maps not available'));
        return;
      }

      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      service.getDetails({
        placeId: placeId,
        fields: ['address_components', 'formatted_address', 'geometry']
      }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          resolve(place);
        } else {
          reject(new Error('Failed to get place details'));
        }
      });
    });
  }

  selectAddressSuggestion(suggestion: any) {
    console.log('SiteInfo - Selected suggestion:', suggestion);
    
    // Handle Google Places selection (need to get details)
    if (suggestion.isGooglePlace && suggestion.place_id) {
      this.setState({ isLoadingAddressSuggestions: true });
      
      this.getGooglePlaceDetails(suggestion.place_id)
        .then((place) => {
          const address = this.parseGooglePlaceDetails(place);
          this.fillAddressFields(suggestion.display_name, address);
        })
        .catch((error) => {
          // console.error('Error getting place details:', error);
          // Fallback to just the display name
          this.fillAddressFields(suggestion.display_name, {});
        })
        .finally(() => {
          this.setState({ isLoadingAddressSuggestions: false });
        });
    } else {
      // Handle Mapbox or other suggestions
      this.fillAddressFields(suggestion.display_name, suggestion.address || {});
    }

    this.setState({
      addressSuggestions: [],
      showAddressSuggestions: false,
    });
  }

  // Shared method to fill address fields
  fillAddressFields(displayName: string, address: any) {
    const { handleChange, onCountrySelect } = this.props;
    
    // Extract address components
    const streetNumber = address.house_number || '';
    const streetName = address.road || '';
    let fullAddress = '';
    
    if (streetNumber && streetName) {
      fullAddress = `${streetNumber} ${streetName}`;
    } else if (streetName) {
      fullAddress = streetName;
    } else {
      // Fallback to first part of display name
      fullAddress = displayName.split(',')[0].trim();
    }
    
    const city = address.city || '';
    const state = address.state || '';
    const country = address.country || '';
    const postalCode = address.postcode || '';

    console.log('SiteInfo - Filling address fields with:', {
      address1: fullAddress,
      city,
      state,
      country,
      postalCode
    });

    // Update address fields
    handleChange("siteInfo", "address1", fullAddress);
    if (city) handleChange("siteInfo", "city", city);
    if (postalCode) handleChange("siteInfo", "postalCode", postalCode);
    
    // Update country and state through proper handlers
    if (country && onCountrySelect) {
      const event = { target: { value: country } };
      onCountrySelect(event, country, false);
      
      // Update state after country is set
      if (state) {
        setTimeout(() => {
          handleChange("siteInfo", "state", state);
        }, 100);
      }
    } else if (state) {
      // Just update state if no country
      handleChange("siteInfo", "state", state);
    }
  }

  handleAddressChange(value: string) {
    console.log('SiteInfo - Address input changed:', value);
    this.props.handleChange("siteInfo", "address1", value);

    if (this.addressSearchTimeout) {
      clearTimeout(this.addressSearchTimeout);
    }

    this.addressSearchTimeout = setTimeout(() => {
      console.log('SiteInfo - Triggering address search after debounce...');
      this.searchAddress(value);
    }, 50);
  }

  hideAddressSuggestions() {
    setTimeout(() => {
      this.setState({ showAddressSuggestions: false });
    }, 200);
  }

  // Address autocomplete functionality
  async searchAddress(query: string) {
    console.log('SiteInfo - Searching for address:', query);
    
    if (!query || query.length < 1) {
      console.log('SiteInfo - Query too short, clearing suggestions');
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    // Check cache first for instant results
    const cacheKey = query.toLowerCase().trim();
    if (this.addressCache.has(cacheKey)) {
      console.log('SiteInfo - Using cached results for:', query);
      const cachedResults = this.addressCache.get(cacheKey) || [];
      this.setState({
        addressSuggestions: cachedResults,
        showAddressSuggestions: cachedResults.length > 0,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    console.log('SiteInfo - Setting loading state...');
    this.setState({ isLoadingAddressSuggestions: true });

    
    if (this.autocompleteService) {
      await this.searchWithGooglePlaces(query);
    } else {
      await this.searchWithGeocoding(query);
    }
  }

  render() {
    const { 
      site, nameErrorState, address1ErrorState, address2ErrorState, cityErrorState, postalCodeErrorState, 
      onRegionSelect, onRegionToggle, isRegionOpen, onCountrySelect, onCountryToggle, isCountryOpen, 
      handleChange, countries, regions
    } = this.props;

    const { addressSuggestions, showAddressSuggestions, isLoadingAddressSuggestions } = this.state;

    console.log('SiteInfo - Rendering with state:', { 
      showAddressSuggestions, 
      suggestionsCount: addressSuggestions.length, 
      isLoading: isLoadingAddressSuggestions 
    });

    return (
      <div>                      
        <Card className="mb-3">
          <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
            <div className="w-100 d-flex justify-content-between">       
            <IconHeading icon={<MapMarkerIcon className="icon-medium" />} heading="Information" />
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              <Col>
                <FormGroup isRequired fieldId="name" className="form-item"
                  helperTextInvalid={nameErrorState?.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={nameErrorState?.validated}>
                  <TextInput className="form-input form-control" validated={nameErrorState?.validated}
                    isRequired type="text" id="name" name="name" placeholder="Site Name"
                    value={site.name} onChange={(e) => handleChange("siteInfo", "name", e)}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col style={{ position: 'relative' }}>
                <FormGroup isRequired fieldId="address1" className="form-item"
                  helperTextInvalid={address1ErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={address1ErrorState.validated}>
                  <TextInput className="form-input form-control" validated={address1ErrorState.validated}
                    isRequired type="text" id="address1" name="address1" placeholder="Start typing site address..."
                    value={site.address1} 
                    onChange={this.handleAddressChange}
                    onBlur={this.hideAddressSuggestions}
                    autoComplete="off"
                  />
                  
                  {/* Address Suggestions Dropdown */}
                  {showAddressSuggestions && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      {isLoadingAddressSuggestions ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                          Loading addresses...
                        </div>
                      ) : (
                        addressSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '12px',
                              cursor: 'pointer',
                              borderBottom: index < addressSuggestions.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                            onClick={() => this.selectAddressSuggestion(suggestion)}
                            onMouseEnter={(e) => {
                              if (e.currentTarget) {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (e.currentTarget) {
                                e.currentTarget.style.backgroundColor = 'white';
                              }
                            }}
                          >
                            <div style={{ fontSize: '14px', fontWeight: '500' }}>
                              {suggestion.display_name}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col>
                <FormGroup fieldId="address2" className="form-item"
                  helperTextInvalid={address2ErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={address2ErrorState.validated}>
                  <TextInput className="form-input form-control" validated={address2ErrorState.validated}
                    type="text" id="address2" name="address2" placeholder="Address Line 2"
                    value={site.address2} onChange={(e) => handleChange("siteInfo", "address2", e)}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col>
                <FormGroup fieldId="city" className="form-item"
                  helperTextInvalid={cityErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={cityErrorState.validated}>
                  <TextInput className="form-input form-control"
                    type="text" id="city" name="city" placeholder="City" validated={cityErrorState.validated}
                    value={site.city} onChange={(e) => handleChange("siteInfo", "city", e)}
                  />
                </FormGroup>
              </Col>

              <Col>
                <FormGroup fieldId="country-toggle" className="form-item">
                  <Input
                    type="select" 
                    value={site.country || ''}
                    onChange={(e) => onCountrySelect(e, e.target.value)}
                    onClick={() => onCountryToggle}
                    open={isCountryOpen} 
                  >
                    {countries &&
                    <>
                      <option key="default" value="">Select Country...</option>
                      {countries.map((country, index) => (
                          <option key={country.key || index} value={country.props.value}>
                              {country.props.value}
                          </option>
                      ))}
                    </>
                    }
                  </Input>
                </FormGroup>
              </Col>
                
              <Col>
                <FormGroup fieldId="region-toggle" className="form-item">
                  <Input
                    type="select" 
                    value={site.state || ''}
                    onChange={(e) => onRegionSelect(e, e.target.value)}
                    onClick={() => onRegionToggle}
                    open={isRegionOpen} 
                  >
                    {regions &&
                    <>
                      <option key="default" value="">Select Region...</option>
                      {regions.map((region, index) => (
                          <option key={region.key || index} value={region.props.value}>
                              {region.props.value}
                          </option>
                      ))}
                    </>
                    }
                  </Input>
                </FormGroup>
              </Col>
                
              <Col>
                <FormGroup fieldId="postalCode" className="form-item"
                  helperTextInvalid={postalCodeErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={postalCodeErrorState.validated}>
                  <TextInput className="form-input form-control"
                    type="text" id="postalCode" name="postalCode" placeholder="Postal Code / Zip" validated={postalCodeErrorState.validated}
                    value={site.postalCode} onChange={(e) => handleChange("siteInfo", "postalCode", e)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
    );
  }
}

export default SiteInfo;
